import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * Collects what /completed needs - RA username and the proof screenshot -
 * and writes the trigger text plus the image into the reply.
 *
 * The RetroAchievements username is optional here for the same reason it
 * is optional on Discord: the bot remembers it after the first time
 * (ra_usernames), and a member who has no RA account can still log a
 * completion unverified. Leaving the field blank produces a bare
 * "/completed", which is exactly what the bot expects.
 *
 * The screenshot goes through fof/upload's own endpoint rather than
 * anything of ours, so the forum's configured storage, size limits, MIME
 * checks and per-group upload permission all apply unchanged - and the
 * markup inserted is whatever that extension's own template produces, so
 * an uploaded image looks identical however it was added.
 *
 * Deliberately does NOT submit the post. It fills in the reply the member
 * is already writing so they can read it back before posting - a
 * completion grants roles and vote weight, so a one-click submit with no
 * chance to check would be the wrong trade.
 */
export default class CompletedModal extends Modal {
  oninit(vnode) {
    var _app$session$user;
    super.oninit(vnode);
    // Prefilled from the profile when the bot has already resolved and
    // verified a handle, so the common case is confirm-and-go.
    this.username = ((_app$session$user = app.session.user) == null ? void 0 : _app$session$user.attribute('raUsername')) || '';
    this.file = null;
    // Optional rating, the forum's version of Discord's "Rate this game"
    // form. Written under the command as "score:" and "review:" lines,
    // which cogs/forum_completed.py reads.
    this.score = '';
    this.review = '';
    this.uploading = false;
    this.error = null;
  }
  className() {
    return 'jrh-CommandModal Modal--small';
  }
  title() {
    return app.translator.trans('jrh-forum-commands.forum.completed.title');
  }
  content() {
    var _app$session$user2;
    const known = (_app$session$user2 = app.session.user) == null ? void 0 : _app$session$user2.attribute('raUsername');
    const canUpload = app.forum.attribute('fof-upload.canUpload');
    return m("div", {
      className: "Modal-body"
    }, m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-forum-commands.forum.completed.help')), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.completed.username_label')), m("input", {
      className: "FormControl",
      type: "text",
      autocomplete: "off",
      placeholder: app.translator.trans('jrh-forum-commands.forum.completed.username_placeholder'),
      value: this.username,
      disabled: this.uploading,
      oninput: e => {
        this.username = e.target.value;
      }
    }), m("p", {
      className: "helpText"
    }, known ? app.translator.trans('jrh-forum-commands.forum.completed.username_known') : app.translator.trans('jrh-forum-commands.forum.completed.username_help'))), canUpload ? m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.completed.screenshot_label')), m("input", {
      className: "FormControl jrh-CommandModal-file",
      type: "file",
      accept: "image/*",
      disabled: this.uploading,
      onchange: e => {
        var _e$target$files;
        this.file = ((_e$target$files = e.target.files) == null ? void 0 : _e$target$files[0]) || null;
        this.error = null;
      }
    }), m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-forum-commands.forum.completed.screenshot_help'))) : m("p", {
      className: "helpText jrh-CommandModal-reminder"
    }, m("i", {
      className: "icon fas fa-camera"
    }), ' ', app.translator.trans('jrh-forum-commands.forum.completed.screenshot_reminder')), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.completed.score_label')), m("input", {
      className: "FormControl",
      type: "text",
      inputmode: "decimal",
      maxlength: "4",
      autocomplete: "off",
      placeholder: "8.5",
      value: this.score,
      disabled: this.uploading,
      oninput: e => {
        this.score = e.target.value;
        this.error = null;
      }
    }), m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-forum-commands.forum.completed.score_help'))), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.completed.review_label')), m("input", {
      className: "FormControl",
      type: "text",
      maxlength: "200",
      autocomplete: "off",
      value: this.review,
      disabled: this.uploading,
      oninput: e => {
        this.review = e.target.value;
      }
    })), this.error ? m("p", {
      className: "helpText jrh-CommandModal-error"
    }, this.error) : null, m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      loading: this.uploading,
      disabled: this.uploading,
      onclick: () => this.submit()
    }, this.uploading ? app.translator.trans('jrh-forum-commands.forum.completed.uploading') : app.translator.trans('jrh-forum-commands.forum.completed.submit'))));
  }
  async submit() {
    const username = (this.username || '').trim();
    // Matches cogs/forum_completed.py's _RA_RE: "username:" then the
    // handle. A bare "/completed" is valid and means "use the handle you
    // already have for me, or none".
    let text = username ? "/completed username:".concat(username) : '/completed';

    // Same rule as Discord's review form: 1-10 in half-point steps, and a
    // review sentence only counts with a score. Checked here so a typo is
    // caught before posting; the bot checks again either way.
    const score = (this.score || '').trim();
    const review = (this.review || '').replace(/\s+/g, ' ').trim();
    if (score) {
      const value = Number(score);
      if (!Number.isFinite(value) || value < 1 || value > 10 || value * 2 % 1 !== 0) {
        this.error = app.translator.trans('jrh-forum-commands.forum.completed.score_invalid');
        return;
      }
      text += "\nscore: ".concat(value);
      if (review) text += "\nreview: ".concat(review);
    } else if (review) {
      this.error = app.translator.trans('jrh-forum-commands.forum.completed.review_needs_score');
      return;
    }
    if (this.file) {
      this.uploading = true;
      this.error = null;
      m.redraw();
      try {
        const markup = await this.upload(this.file);
        // Blank line so the image renders as its own block rather than
        // running into the command text, which the bot's trigger regex
        // would still match but which reads badly in the post.
        if (markup) text += "\n\n".concat(markup);
      } catch (e) {
        // Stop rather than insert a completion with no proof attached -
        // the member asked for the screenshot to be included, and silently
        // dropping it would be worse than making them retry.
        this.uploading = false;
        this.error = app.translator.trans('jrh-forum-commands.forum.completed.upload_failed');
        m.redraw();
        return;
      }
      this.uploading = false;
    }
    this.attrs.oninsert(text);
    this.hide();
  }

  /**
   * Uploads through fof/upload's own endpoint and returns the markup its
   * template produces for the stored file.
   *
   * `files[]` is the field name that extension's own uploader uses, and
   * `bbcode` is the attribute it inserts into the composer - taking both
   * from it rather than reimplementing means a change to the forum's
   * upload template is picked up here for free.
   */
  async upload(file) {
    var _response$data;
    const body = new FormData();
    body.append('files[]', file);
    const response = await app.request({
      method: 'POST',
      url: "".concat(app.forum.attribute('apiUrl'), "/fof/upload"),
      serialize: raw => raw,
      body
    });
    const first = response == null || (_response$data = response.data) == null || (_response$data = _response$data[0]) == null ? void 0 : _response$data.attributes;
    if (!first) throw new Error('no file returned');
    return first.bbcode || "![](".concat(first.url, ")");
  }
}
flarum.reg.add('jrh-forum-commands', 'forum/components/CompletedModal', CompletedModal);